<template>
  <div class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingAgencies" class="accordion-header">
        <button
          aria-controls="collapseAgencies"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseAgencies"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="template-setup-accordion-icon me-8 d-flex align-items-center justify-content-center">
              <v-icon icon="contact" size="xs" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("template.agency.title") }}</div>
              <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("template.agency.subtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseAgencies"
        aria-labelledby="headingAgencies"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionExample"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <p class="text-xs fw-medium text-muted text-uppercase mb-8">{{ $t("template.agency.editAgenciesBelongsTo") }}</p>

          <div class="agencies-container mb-12">
            <template v-if="!!template.agencies && template.agencies.length > 0">
              <div v-for="agency in template.agencies" :key="agency.id" class="agency-item d-flex align-items-center mb-4 border rounded-xs overflow-hidden bg-light bg-opacity-10">
                <div class="template-setup-agency-avatar d-flex align-items-center justify-content-center fw-bold">
                  {{ agency.name.charAt(0) }}
                </div>

                <div class="agency-info flex-grow-1 px-8 py-2 d-flex justify-content-between align-items-center">
                  <div class="text-xs fw-medium text-dark">{{ agency.name }}</div>

                  <div class="actions-container" v-if="can('template.detach')">
                    <v-button
                      :disabled="disassociatingAgencyToTemplate"
                      class="btn-icon p-2 rounded-circle"
                      variant="icon"
                      @click="detachAgency(agency)"
                    >
                      <v-icon icon="trash" size="xs" class="text-danger" />
                    </v-button>
                  </div>
                </div>
              </div>
            </template>

            <div v-else class="text-xs text-muted italic">{{ $t("template.agency.noAgencies") }}</div>
          </div>

          <template v-if="can('template.attach')">
            <div class="search-separator-container pb-16 mb-4">
              <div class="separator-text-container">
                <span class="separator-text text-xs">{{ $t("template.agency.addAgency") }}</span>
              </div>
            </div>

            <form id="template-agencies-search-form" class="d-flex flex-column justify-content-between h-100">
              <fieldset>
                <legend class="visually-hidden">{{ $t("template.agency.title") }}</legend>

                <v-select-template-agency-search
                  id="template-agencies-search"
                  :selected-options="template.agencies"
                  @selected="assignAgency"
                />
              </fieldset>
            </form>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import Template from "@/api/objects/Template";
import VSelectTemplateAgencySearch from "@/components/vendor/VSelectTemplateAgencySearch.vue";
import Agency from "@/api/objects/Agency";
import api from "@/api";
import useCapabilities from "@/helpers/capabilities";
import { computed, getCurrentInstance, ref } from "vue";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  agency: string;
  template: Template;
}>();
const emit = defineEmits(["reloadTemplate"]);
const { can } = useCapabilities();
const toast = useToast();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const { t } = useI18n();

const disableButton = ref<boolean>(false);
const disassociatingAgencyToTemplate = ref(false);

const selectedAgencies = computed(() => {
  return props.template.agencies.map((agency: any) => agency);
});

const assignAgency = async (agency: { id: number; name: string }) => {
  await $modal.confirm({
    title: t("template.agency.addAgencyToTemplateTitle"),
    text: t("template.agency.addAgencyToTemplateTitleText", {
      agency: agency.name,
      template: props.template.name,
    }),
    leftButtonClasses: "btn-tertiary",
    confirmButtonText: t("general.button.add"),
    confirmButtonCallback: () => doAssignAgency(agency.id),
  });
};

const doAssignAgency = async (agencyId: number) => {
  try {
    const data = {
      agencies: [agencyId],
    };

    await api.activityService.attachAgencies(props.template.id, data);

    toast.success(t("general.shared.savedChanges"), {
      position: "bottom-right",
    });
    emit("reloadTemplate");
  } catch (e: any) {
    console.error(e.response?.data);
    toast.error(e.response?.data?.message || "An error occurred.", {
      position: "bottom-right",
    });
  }
};

const detachAgency = async (agency: Agency) => {
  await $modal.delete({
    title: t("template.agency.detachAgencyToTemplateTitle"),
    text: t("template.agency.detachAgencyToTemplateTitleText", {
      agency: agency.name,
      template: props.template.name,
    }),
    deleteButtonText: t("general.button.remove"),
    deleteButtonLoadingText: t("general.button.removing"),
    rightButtonClasses: "btn-tertiary",
    deleteButtonCallback: () => doDetachAgency(agency.id),
  });
};

const doDetachAgency = async (agencyId: number) => {
  try {
    disassociatingAgencyToTemplate.value = true;

    const data = {
      agencies: [agencyId],
    };

    await api.activityService.detachAgencies(props.template.id, data);

    toast.success(t("general.shared.savedChanges"), {
      position: "bottom-right",
    });
    emit("reloadTemplate");
  } catch (e: any) {
    console.error(e.response?.data);
    toast.error(e.response?.data?.message || "An error occurred.", {
      position: "bottom-right",
    });
  } finally {
    disassociatingAgencyToTemplate.value = false;
  }
};
</script>

<style lang="scss">
.agencies-container {
  .agency-item {
    transition: all 0.2s ease;
    &:hover {
      background-color: var(--uq-light) !important;
      border-color: var(--uq-primary) !important;
    }
  }
}

:deep(.accordion-button) {
  padding: 0.5rem 1rem !important;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.9375rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.8125rem !important;
}
</style>
